import {appApi} from "@services/api.service";
import {AvailabilityForm, TimeDurationOption} from "@models/availability";
import moment from 'moment';

const endpointPrefix = 'worker/availabilities';

const addAvailability = async (data: AvailabilityForm) => {
    return appApi.post(endpointPrefix, data);
}

const updateAvailability = async (id: number, data: AvailabilityForm, selectedDate: string) => {
    return appApi.put(endpointPrefix + '/' + id, data, {
        params: { selectedDate: selectedDate}
    });
}

const removeAvailability = async (id: number, selectedDate: string, removeNextRepeats: boolean = false) => {
    return appApi.delete(`${endpointPrefix}/${id}`, {
        params: {
            selectedDate: selectedDate,
            removeNextRepeats: removeNextRepeats ? 1: 0,
        },
    });
}

const getAvailabilityPreferredPlacesOfService = async (workerAvailabilityId?: number) => {
    const url = workerAvailabilityId
        ? `${endpointPrefix}/preferred-places-of-service?workerAvailabilityId=${workerAvailabilityId}`
        : `${endpointPrefix}/preferred-places-of-service`;

    const { data } = await appApi.get(url);
    return data;
};

const getTimeDurationOptions = () => {
    const options: TimeDurationOption[] = [];

    let totalMinutes = 15;

    while (totalMinutes <= 24 * 60) {
        const hours = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;

        const label = `${hours > 0 ? `${hours}h ` : ''}${mins > 0 ? `${mins}min` : ''}`.trim();

        options.push({ label, value: totalMinutes });
        totalMinutes += 15;
    }

    return options;
}

const getRepeatTypeOptions = () => {
    return [
        {
            key: 'DONT_REPEAT',
            label: 'availability.repeat.dont_repeat'
        },
        {
            key: 'DAILY',
            label: 'availability.repeat.daily'
        },
        {
            key: 'WEEKLY',
            label: 'availability.repeat.weekly'
        },
        {
            key: 'WEEKDAYS',
            label: 'availability.repeat.weekdays'
        },
    ]
}

const formatDurationHuman = (totalMinutes: number): string => {
    if (!totalMinutes || totalMinutes <= 0) return '';

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const hoursPart = hours > 0 ? `${hours}h` : '';
    const minutesPart = minutes > 0 ? `${minutes}min` : '';

    return [hoursPart, minutesPart].filter(Boolean).join(' ');
};

const calculateMaxRepeatEndDate = (startTime: string): string => {
    return moment(startTime)
        .add(1, 'month')
        .endOf('month')
        .format('YYYY-MM-DD');
};

const getAvailabilityErrorMessage = (error: any, isEdit: boolean): string => {
    const errorCode = error?.response?.data?.message;

    if (errorCode === 'WORKER_HAS_AVAILABILITY') {
        return 'availability.error.workerHasAvailability';
    }

    if (errorCode === 'HOURS_FOR_AVAILABILITY_EXCEEDED') {
        return isEdit ? 'availability.error.hoursForAvailabilityEditExceeded' : 'availability.error.hoursForAvailabilityAddExceeded';
    }

    return isEdit ? 'availability.error.edit' : 'availability.error.add';
};

const canEditAvailability = (startTime: string, thresholdHours: number = 48): boolean => {
    return moment(startTime).isAfter(moment().add(thresholdHours, 'hours'));
};

const canEditPreferredPlaces = (startTime: string): boolean => {
    const startDate = moment(startTime).format('YYYY-MM-DD');
    const today = moment().format('YYYY-MM-DD');
    return startDate === today || moment(startTime).isAfter(moment());
};

const arePreferredPlacesModified = (currentIds: number[], initialIds: number[], currentAgreement: boolean, initialAgreement: boolean): boolean => {
    const idsChanged = JSON.stringify(currentIds.sort()) !== JSON.stringify(initialIds.sort());
    const agreementChanged = currentAgreement !== initialAgreement;

    return idsChanged || agreementChanged;
};

export {
    addAvailability,
    updateAvailability,
    removeAvailability,
    getAvailabilityPreferredPlacesOfService,
    getTimeDurationOptions,
    getRepeatTypeOptions,
    formatDurationHuman,
    calculateMaxRepeatEndDate,
    getAvailabilityErrorMessage,
    canEditAvailability,
    canEditPreferredPlaces,
    arePreferredPlacesModified,
};
